import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Wallet, CreditCard, History, Crown, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { WalletBalance } from "@/components/Billing/WalletBalance";
import { AddPaymentMethodDialog } from "@/components/Billing/AddPaymentMethodDialog";
import { PaymentMethodCard } from "@/components/Billing/PaymentMethodCard";
import { TopUpDialog } from "@/components/Billing/TopUpDialog";
import { AutoReloadSettings } from "@/components/Billing/AutoReloadSettings";
import { AutoReloadPromoDialog, canOfferAutoReload, takeAutoReloadPromo } from "@/components/Billing/AutoReloadPromoDialog";
import { SubscriptionUpgrade } from "@/components/Billing/SubscriptionUpgrade";
import { UsageHistory } from "@/components/Billing/UsageHistory";
import {
  useWorkspaceBilling,
  useWorkspaceBillingTransactions,
  useCheckoutStatus,
  useCanManageBilling,
} from "@/hooks/use-workspace-billing";
import { useWorkspaceSubscription, isSubscriptionEntitled } from "@/hooks/use-workspace-subscription";
import { useWallet } from "@/hooks/use-wallet";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { formatMinutes } from "@/lib/money";

export default function Billing() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const {
    data: workspaceBilling,
    isLoading: billingLoading,
    error: billingError,
    refetch: refetchWorkspace,
  } = useWorkspaceBilling();
  const {
    data: transactionPages,
    isLoading: transactionsLoading,
    isError: transactionsError,
    hasNextPage: hasMoreTransactions,
    fetchNextPage: fetchMoreTransactions,
    isFetchingNextPage: isFetchingMoreTransactions,
  } = useWorkspaceBillingTransactions();
  const transactions = transactionPages?.pages.flatMap(page => page.items);
  const { data: subscription } = useWorkspaceSubscription();
  const isPro = isSubscriptionEntitled(subscription);
  const { freeMinutes } = useWallet();
  const canManage = useCanManageBilling();
  const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
  // One state so a preset from "Add funds now" is dropped when the dialog closes.
  const [topUp, setTopUp] = useState<{ open: boolean; preset?: number }>({ open: false });
  const openTopUp = (preset?: number) => setTopUp({ open: true, preset });
  const [tab, setTab] = useState('wallet');
  // Set by a deep link below; cleared once the section it names is on screen.
  const [scrollTo, setScrollTo] = useState<string | null>(null);

  // `?topup=1` is the low-balance email's one-click link: open the dialog once
  // and strip the param so a refresh doesn't reopen it.
  useEffect(() => {
    if (searchParams.get('topup') !== '1') return;
    setTopUp({ open: true });
    const rest = new URLSearchParams(searchParams);
    rest.delete('topup');
    navigate({ pathname: window.location.pathname, search: rest.toString() }, { replace: true });
  }, [searchParams, navigate]);

  // The billing emails' other one-click links, handled the same way: open the
  // section the email is about, then strip the param so a refresh doesn't
  // replay it. `?pm=1` also opens the add-card flow, as `?topup=1` opens the
  // top-up dialog; the rest only select their tab and scroll to it.
  useEffect(() => {
    const rest = new URLSearchParams(searchParams);
    const took = (key: string, value: string) => {
      if (searchParams.get(key) !== value) return false;
      rest.delete(key);
      return true;
    };

    if (took('pm', '1')) {
      setTab('payment');
      setShowAddPaymentMethod(true);
      setScrollTo('billing-payment-methods');
    }
    if (took('autoreload', '1')) {
      setTab('wallet');
      setScrollTo('billing-auto-reload');
    }
    if (took('plan', '1')) {
      setTab('subscription');
      setScrollTo('billing-subscription');
    }
    if (took('tab', 'history')) {
      setTab('history');
      setScrollTo('billing-history');
    }

    if (rest.toString() === searchParams.toString()) return;
    navigate({ pathname: window.location.pathname, search: rest.toString() }, { replace: true });
  }, [searchParams, navigate]);

  // The scroll waits for the render the link asked for: a section lives inside a
  // tab that only mounts while selected, and nothing is mounted at all while
  // billing is still loading.
  useEffect(() => {
    if (!scrollTo || billingLoading) return;
    setScrollTo(null);
    document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [scrollTo, billingLoading]);

  // Handle return from Stripe Checkout. The params are stripped before anything
  // else so a refresh (or a StrictMode remount) can't replay the confirmation.
  const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null);
  const handledCheckoutReturn = useRef(false);
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [promoArmed, setPromoArmed] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);

  useEffect(() => {
    if (handledCheckoutReturn.current) return;

    const checkoutResult = searchParams.get('checkout');
    if (!checkoutResult) return;

    const sessionId = searchParams.get('session_id');
    handledCheckoutReturn.current = true;
    navigate(window.location.pathname, { replace: true });

    if (checkoutResult === 'success' && sessionId) {
      setCheckoutSessionId(sessionId);
      // Armed before the redirect, because this return is a cold page load: the
      // billing cache that knew they had no card did not survive the navigation.
      setPromoArmed(takeAutoReloadPromo(workspaceId));
    } else if (checkoutResult === 'cancelled') {
      toast({
        variant: "destructive",
        title: "Payment cancelled",
        description: "You cancelled the payment. No charges were made.",
      });
    }
  }, [searchParams, navigate, toast, workspaceId]);

  // The wallet is credited by the Stripe webhook, so this polls for the credit
  // instead of assuming the redirect means success.
  const { isConfirming } = useCheckoutStatus(checkoutSessionId);

  // The same webhook credits the wallet before it stores the card's country, so
  // the row can briefly show a saved card with no issuer yet — and the issuer is
  // what picks the offer's wording. Wait for it rather than reading once.
  useEffect(() => {
    if (!promoArmed) return;
    const deadline = Date.now() + 30_000;

    const timer = setInterval(async () => {
      const { data } = await refetchWorkspace();
      const expired = Date.now() >= deadline;
      const landed = data?.payment_method_added && (data.payment_method_country !== null || expired);
      if (!landed && !expired) return;
      clearInterval(timer);
      setPromoArmed(false);
      if (data && canOfferAutoReload(data)) setPromoOpen(true);
    }, 2_000);

    return () => clearInterval(timer);
  }, [promoArmed, refetchWorkspace]);

  if (billingLoading) {
    return (
      <div className="h-full overflow-auto bg-gradient-to-b from-background to-muted/20">
        <div className="max-w-5xl mx-auto px-4 py-8 md:px-8 space-y-8">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-48 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  if (billingError || !workspaceBilling) {
    const errorMessage = billingError instanceof Error
      ? billingError.message
      : "We couldn't load this workspace's billing information.";

    return (
      <div className="h-full overflow-auto bg-gradient-to-b from-background to-muted/20">
        <div className="max-w-5xl mx-auto px-4 py-8 md:px-8 space-y-8">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">Billing & Subscription</h1>
            <p className="text-muted-foreground text-lg">
              Manage your wallet, payment methods, and subscription
            </p>
          </div>
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive">Couldn't load billing</CardTitle>
              <CardDescription>{errorMessage}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Please try again. If the issue persists, contact support.
              </p>
              <Button onClick={() => refetchWorkspace()} variant="outline">
                Try again
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gradient-to-b from-background to-muted/20">
      <div className="max-w-5xl mx-auto px-4 py-8 md:px-8 space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Billing & Subscription</h1>
          <p className="text-muted-foreground text-lg">
            Manage your wallet, payment methods, and subscription
          </p>
        </div>

        {isConfirming && (
          <Alert>
            <Loader2 className="h-4 w-4 animate-spin" />
            <AlertDescription>
              Confirming your payment… your wallet will update as soon as it clears.
            </AlertDescription>
          </Alert>
        )}

        {canManage === false && (
          <Alert>
            <AlertDescription>
              You can view this workspace's billing. Only its owner or an admin can add funds, change
              auto-reload, or manage the card and plan.
            </AlertDescription>
          </Alert>
        )}

        {/* Wallet Balance Card */}
        <WalletBalance />

        {/* Subscription Status Cards */}
        {!isPro && !workspaceBilling.payment_method_added && freeMinutes > 0 && (
          <Card className="border-amber-200/50 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 dark:border-amber-800/50">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                  <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <CardTitle className="text-amber-800 dark:text-amber-200 text-lg">
                    Trial Account
                  </CardTitle>
                  <CardDescription className="text-amber-700 dark:text-amber-300">
                    You have {formatMinutes(freeMinutes)} free minutes remaining. Add a payment method to continue after your trial.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>
        )}

        {isPro && (
          <Card className="border-primary/30 bg-gradient-to-r from-primary/5 via-background to-primary/10">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Pro Plan Active</CardTitle>
                    <CardDescription>
                      Access to all features and {workspaceBilling.call_concurrency_limit ?? 10} concurrent calls
                    </CardDescription>
                  </div>
                </div>
                <Badge className="bg-gradient-to-r from-primary to-primary/80 shadow-sm">Pro</Badge>
              </div>
            </CardHeader>
          </Card>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="w-full max-w-2xl grid grid-cols-4 h-12 p-1 bg-muted/50">
            <TabsTrigger 
              value="wallet" 
              className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg transition-all"
            >
              <Wallet className="w-4 h-4" />
              <span className="hidden sm:inline">Wallet</span>
            </TabsTrigger>
            <TabsTrigger 
              value="payment" 
              className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg transition-all"
            >
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Payment</span>
            </TabsTrigger>
            <TabsTrigger 
              value="subscription" 
              className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg transition-all"
            >
              <Crown className="w-4 h-4" />
              <span className="hidden sm:inline">Subscription</span>
            </TabsTrigger>
            <TabsTrigger 
              value="history" 
              className="flex items-center gap-2 data-[state=active]:bg-background data-[state=active]:shadow-sm rounded-lg transition-all"
            >
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
          </TabsList>

          <div className="mt-6">
            <TabsContent value="wallet" className="m-0 space-y-6">
              <Card className="border-muted/50">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                      <Wallet className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Top Up Wallet</CardTitle>
                      <CardDescription>
                        Add credits to your wallet. Minimum top-up is $20.
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <TopUpDialog
                    open={topUp.open}
                    onOpenChange={(open) => setTopUp({ open })}
                    presetAmount={topUp.preset}
                  />
                  <div id="billing-auto-reload" className="border-t border-border/50 pt-6">
                    <AutoReloadSettings onAddFunds={openTopUp} />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="payment" id="billing-payment-methods" className="m-0">
              <Card className="border-muted/50">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-muted-foreground" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">Payment Methods</CardTitle>
                      <CardDescription>
                        Manage your payment methods for wallet top-ups and subscriptions
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <AddPaymentMethodDialog open={showAddPaymentMethod} onOpenChange={setShowAddPaymentMethod} />
                  <PaymentMethodCard 
                    paymentMethodAdded={workspaceBilling.payment_method_added}
                    onRemoved={() => refetchWorkspace()}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="subscription" id="billing-subscription" className="m-0">
              <SubscriptionUpgrade />
            </TabsContent>

            <TabsContent value="history" id="billing-history" className="m-0">
              <UsageHistory
                transactions={transactions || []}
                isLoading={transactionsLoading}
                isError={transactionsError}
                hasMore={!!hasMoreTransactions}
                isLoadingMore={isFetchingMoreTransactions}
                onLoadMore={() => fetchMoreTransactions()}
              />
            </TabsContent>
          </div>
        </Tabs>

        {/* Outside the tabs: inactive TabsContent unmounts, and this has to be
            able to open whichever tab the customer lands back on. */}
        <AutoReloadPromoDialog open={promoOpen} onOpenChange={setPromoOpen} billing={workspaceBilling} />
      </div>
    </div>
  );
}
